import { EntityConfig } from './types';
import { Badge } from '@/components/ui/badge';
import { Building } from 'lucide-react';

export interface Company {
  id: string;
  name: string;
  industry: string | null;
  category: string | null;
  crm_status: string | null;
  description: string | null;
  website_url: string | null;
  linkedin_url: string | null;
  primary_location: string | null;
  secondary_locations: string[] | null;
  employee_count_estimate: number | null;
  created_at: string;
  updated_at: string | null;
}

const CATEGORY_OPTIONS = [
  { label: 'Services', value: 'services' },
  { label: 'Staffing', value: 'staffing' },
  { label: 'End Client', value: 'end_client' },
  { label: 'Product', value: 'product' },
  { label: 'Consulting', value: 'consulting' },
];

const CRM_STATUS_OPTIONS = [
  { label: 'Lead', value: 'lead' },
  { label: 'Prospect', value: 'prospect' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'Lost', value: 'lost' },
];

const getCrmStatusVariant = (status: string | null) => {
  switch (status) {
    case 'active': return 'default';
    case 'prospect': return 'secondary';
    case 'lead': return 'outline';
    case 'inactive': return 'secondary';
    case 'lost': return 'destructive';
    default: return 'secondary';
  }
};

export const companiesConfig: EntityConfig<Company> = {
  name: 'companies',
  singularName: 'Company',
  pluralName: 'Companies',
  tableName: 'companies',
  keyField: 'id',
  icon: <Building className="h-5 w-5" />,
  description: 'Manage company profiles and intelligence',
  
  permissionModule: 'companies',
  
  defaultSelect: '*',
  defaultOrderBy: { field: 'name', ascending: true },
  searchFields: ['name', 'industry', 'primary_location'],
  defaultPageSize: 25,
  
  columns: [
    {
      id: 'name',
      header: 'Company Name',
      accessorKey: 'name',
      sortable: true,
    },
    {
      id: 'industry',
      header: 'Industry',
      accessorKey: 'industry',
      cell: (row) => row.industry || '-',
      sortable: true,
    },
    {
      id: 'category',
      header: 'Category',
      accessorKey: 'category',
      cell: (row) => (
        <Badge variant="outline" className="capitalize">
          {row.category?.replace('_', ' ') || '-'}
        </Badge>
      ),
      sortable: true,
    },
    {
      id: 'crm_status',
      header: 'CRM Status',
      accessorKey: 'crm_status',
      cell: (row) => (
        <Badge variant={getCrmStatusVariant(row.crm_status)} className="capitalize">
          {row.crm_status || 'Unknown'}
        </Badge>
      ),
      sortable: true,
    },
    {
      id: 'location',
      header: 'Location',
      accessorKey: 'primary_location',
      cell: (row) => row.primary_location || '-',
      sortable: true,
    },
    {
      id: 'employees',
      header: 'Employees',
      accessorKey: 'employee_count_estimate',
      cell: (row) => row.employee_count_estimate?.toLocaleString() || '-',
      sortable: true,
      defaultVisible: false,
    },
  ],
  
  formFields: [
    {
      name: 'name',
      label: 'Company Name',
      type: 'text',
      required: true,
      placeholder: 'Enter company name',
      gridSpan: 2,
    },
    {
      name: 'industry',
      label: 'Industry',
      type: 'text',
      placeholder: 'e.g. Technology, Healthcare',
      gridSpan: 1,
    },
    {
      name: 'category',
      label: 'Category',
      type: 'select',
      options: CATEGORY_OPTIONS,
      gridSpan: 1,
    },
    {
      name: 'crm_status',
      label: 'CRM Status',
      type: 'select',
      options: CRM_STATUS_OPTIONS,
      defaultValue: 'lead',
      gridSpan: 1,
    },
    {
      name: 'employee_count_estimate',
      label: 'Employee Count',
      type: 'number',
      placeholder: '100',
      gridSpan: 1,
    },
    {
      name: 'primary_location',
      label: 'Primary Location',
      type: 'text',
      placeholder: 'e.g. San Francisco, CA',
      gridSpan: 1,
    },
    {
      name: 'website_url',
      label: 'Website',
      type: 'url',
      placeholder: 'https://...',
      gridSpan: 1,
    },
    {
      name: 'linkedin_url',
      label: 'LinkedIn',
      type: 'url',
      placeholder: 'https://linkedin.com/company/...',
      gridSpan: 2,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      placeholder: 'Company description...',
      gridSpan: 2,
    },
  ],
  
  filters: [
    {
      id: 'category',
      label: 'Category',
      field: 'category',
      type: 'select',
      options: [
        { label: 'All Categories', value: 'all' },
        ...CATEGORY_OPTIONS,
      ],
      defaultValue: 'all',
    },
    {
      id: 'crm_status',
      label: 'CRM Status',
      field: 'crm_status',
      type: 'select',
      options: [
        { label: 'All Status', value: 'all' },
        ...CRM_STATUS_OPTIONS,
      ],
      defaultValue: 'all',
    },
  ],
  
  audit: {
    enabled: true,
    excludeFields: ['updated_at'],
    detailedChangelog: true,
  },
  
  notifications: {
    onCreate: {
      eventType: 'company_created',
      recipients: 'admins',
    },
    onUpdate: {
      eventType: 'company_updated',
      recipients: 'admins',
      onlyForFields: ['crm_status'],
    },
  },
  
  routes: {
    list: '/recruiter/companies',
    detail: '/recruiter/companies/:id',
  },
  
  features: {
    canCreate: true,
    canUpdate: true,
    canDelete: true,
    canBulkDelete: false,
    canExport: true,
    canSearch: true,
    showActivityLog: true,
  },
};
